import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { get } from 'global/lib/lodash';
import { ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import ReportsIcon from '@mui/icons-material/Assignment';
import Tooltip from '@mui/material/Tooltip';
import LotIcon from '@mui/icons-material/Home';
import { uppercaseFirst } from 'global/utils';
import TypoLabel from 'global/components/typo-label';
import { Loading } from 'global/styles';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import { getAssessmentLockIcon } from 'feature/report/assessmentLock';
import { useUser } from 'global/hooks/useUser';
import ScoreIcon from 'global/components/ScoreIcon';
import { RlIcon, HideXSmall, DisplayScores, RlScore } from './ReportList.styles';
import { getRealmName } from 'global/utils';

const Location = ({ realm, realmType }) => {
  return (
    <>
      {realmType === 'lot' ? (
        <RlIcon as={LotIcon} />
      ) : realmType === 'community' ? (
        <RlIcon as={CommunityIcon} />
      ) : realmType === 'division' ? (
        <RlIcon as={DivisionIcon} />
      ) : (
        <RlIcon as={ReportsIcon} />
      )}
      {uppercaseFirst(realm)}
    </>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const { item, onSelect, ListIcon, alwaysShowScore, alwaysShowIcon, scoreLabel = 'Score:' } = props;
  const { me, userBuilderRealm } = useUser();
  const LockIcon = ListIcon ? null : getAssessmentLockIcon(item, me, userBuilderRealm);
  const LeadingIcon = ListIcon || LockIcon || ReportsIcon;
  const content = {
    ...item,
    title: get(item, 'name', Loading),
    realm: getRealmName(item),
    realmType: get(item, 'realm.tier', Loading),
    startDate: get(item, 'startDate', Loading),
    auditor: get(item, 'authors.0.name', get(item, 'authors.0.email', '-unknown-')),
    scores: get(item, 'scores', Loading),
    status: get(item, 'status', Loading),
  };

  const getScoreNode = () => {
    if (!alwaysShowScore && content.status === 'draft') {
      return 'Draft';
    } else if (!alwaysShowScore && content.status === 'review') {
      return 'Under Review';
    } else {
      return <ScoreDisplay scores={content.scores ? content.scores : {}} scoreLabel={scoreLabel} />;
    }
  };

  const icon = ListIcon ? <LeadingIcon color="secondary" /> : <LeadingIcon />;
  const buttonProps = onSelect
    ? { onClick: (e) => onSelect(item, e) }
    : { component: Link, to: `/reports/assessment/${content.id}` };

  return (
    <>
      <Tooltip title={LockIcon ? `Locked by: ${get(item, 'lockedBy.0.name', '')}` : ''} placement="right">
        <ListItemButton {...buttonProps} data-testid="assessment-list-item" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {alwaysShowIcon ? <ListItemIcon>{icon}</ListItemIcon> : <HideXSmall as={ListItemIcon}>{icon}</HideXSmall>}
          <ListItemText
            sx={{ flex: 1, minWidth: 0 }}
            primary={content.title}
            slotProps={{ secondary: { component: 'div' } }}
            secondary={
              <>
                <TypoLabel hideSmallPrimary="true" primary={'Location: '} secondary={<Location realm={content.realm} realmType={content.realmType} />} />
                <TypoLabel
                  secondary={[moment(content.startDate).isValid() ? moment(content.startDate).format('MM/DD/YYYY') : null, content.auditor]
                    .filter(Boolean)
                    .join(', ')}
                />
              </>
            }
          />
          <DisplayScores sx={{ flexShrink: 0, alignSelf: 'center' }}>
            {getScoreNode()}
          </DisplayScores>
        </ListItemButton>
      </Tooltip>
    </>
  );
};

const ScoreDisplay = ({ scores, scoreLabel = 'Score:' }) => {
  const scorecardIds = Object.keys(scores || {}).filter((id) => scores[id] && typeof scores[id] === 'object');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {scorecardIds.map((scorecardId) => {
        const score = scores[scorecardId].calculatedScore;
        return (
          <RlScore key={scorecardId}>
            <HideXSmall>{scoreLabel}&nbsp;</HideXSmall>
            {`${scores[scorecardId].displayValue ?? ''}`}
            <ScoreIcon score={score} />
          </RlScore>
        );
      })}
    </div>
  );
};
